import React, { useState, useEffect } from 'react'
import { Select, Space } from 'antd'
import { ClockCircleOutlined, CloseCircleFilled, SwapRightOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { generateTimeOptions, getMinIntervalMinutes } from './generate-timepicker-time-options'
import IntlMessages from '../utils/IntlMessages'
import { BaseInfo } from 'rc-picker/lib/interface'
import { CustomTimeRangePickerProps, ITimeOption } from './types'
import { DEFAULT_WORKING_HOURS_IN_DAY } from '../../data/app-parameters'
import { getHalfDayDurationInMinutes, getUserWorkingHoursDependingOnType, getUserWorkingHoursOnDay } from '../../functions/work-week'
import { ShortestLeaveIntervalEnum } from '../../types/leave-policy'
import { HourFormatEnum } from '../../types/user'
import { safeToISOString, safeDayjs } from '../../functions/safe-date-format'

const CustomTimeRangePicker: React.FC<CustomTimeRangePickerProps> = ({
  form,
  shortestLeaveInterval,
  workHours,
  selectedDate,
  shouldResetTime,
  hourFormat,
  minuteStep = 60,
  isHalfDayHoliday,
  onChange,
  style,
  editTimes,
  setShouldResetTime,
}) => {
  const [startTime, setStartTime] = useState<ITimeOption | null>(null)
  const [endTime, setEndTime] = useState<ITimeOption | null>(null)
  const [hovered, setHovered] = useState<boolean>(false)
  const [hourFormatString] = useState<string>(hourFormat === HourFormatEnum.twelve ? 'hh:mm A' : 'HH:mm')
  const [startTimes, setStartTimes] = useState<ITimeOption[]>([])
  const [endTimes, setEndTimes] = useState<ITimeOption[]>([])

  // Resolves the "day length" used to bound the end-time picker. Semantics differ by interval:
  //   - halfDay / fullDay:  needs the user's actual working hours (halfDay = workHours/2).
  //     Falls back to DEFAULT_WORKING_HOURS_IN_DAY when the schedule returns 0 (non-working day, or
  //     workHours not loaded yet on mobile) so halfDay end doesn't collapse onto the start time.
  //   - hourly variants:    "specific hours worked" - there is no natural cap tied to the schedule,
  //     so we allow the full 24h calendar day. Relevant for TOIL where a user may log overtime
  //     that exceeds their normal shift length.
  const getWorkingHoursForEndCompute = (): number => {
    const isHourly =
      shortestLeaveInterval === ShortestLeaveIntervalEnum.oneHour ||
      shortestLeaveInterval === ShortestLeaveIntervalEnum.halfHour ||
      shortestLeaveInterval === ShortestLeaveIntervalEnum.fifteenMinutes
    if (isHourly) return 24
    if (!selectedDate) return DEFAULT_WORKING_HOURS_IN_DAY
    const real = getUserWorkingHoursOnDay(getUserWorkingHoursDependingOnType(workHours) || [], selectedDate.toDate())
    return real > 0 ? real : DEFAULT_WORKING_HOURS_IN_DAY
  }

  // Hosts pass editTimes as an inline literal, so its reference changes on every parent
  // render. Effects key on this primitive fingerprint instead, otherwise any host
  // re-render (e.g. the loading flip while saving) reverts user-picked times to the
  // stored leave's times.
  const editTimesKey = editTimes ? `${editTimes.startHour}:${editTimes.startMinute ?? 0}-${editTimes.endHour}:${editTimes.endMinute ?? 0}` : null

  useEffect(() => {
    if (editTimes && selectedDate) {
      const { startHour, startMinute, endHour, endMinute } = editTimes
      // Zero seconds/ms: end-option enablement compares minute diffs against this start,
      // and stray seconds from "now" truncate the diff, disabling every option.
      const startTime = dayjs()
        .hour(startHour)
        .minute(startMinute as number)
        .second(0)
        .millisecond(0)
      const endTime = dayjs()
        .hour(endHour)
        .minute(endMinute as number)
        .second(0)
        .millisecond(0)
      const startTimeObj = {
        key: safeToISOString(startTime),
        label: startTime.format(hourFormatString),
        value: safeToISOString(startTime),
      }

      // Preserve the "24:00" / "12:00 AM" label when restoring a leave that ended at
      // end-of-day (dayjs rolls hour=24 into next day 00:00, so a raw .format() would
      // read as today's midnight instead).
      const endsAtMidnight = endHour === 24 || endTime.isAfter(startTime, 'day')
      const midnightLabel = hourFormatString.includes('A') ? '12:00 AM' : '24:00'
      const endTimeObj = {
        key: safeToISOString(endTime),
        label: endsAtMidnight ? midnightLabel : endTime.format(hourFormatString),
        value: safeToISOString(endTime),
      }
      setStartTime(startTimeObj)
      const startTimeValue = safeDayjs(startTimeObj.value, startTime)

      const endTimeOptions = generateTimeOptions({
        type: 'end',
        shortestLeaveInterval,
        startTimeValue,
        hourFormatString,
        minuteStep,
        workingHoursInDay: getWorkingHoursForEndCompute(),
        isHalfDayHoliday: Boolean(isHalfDayHoliday()),
      })
      setEndTimes(endTimeOptions)
      // Set synchronously: deferring this (setTimeout) let the startTime effect below run
      // with endTime still null, auto-pick the first option, and write it into the form
      // while the display later showed the stored end - an unchanged submit then saved
      // the wrong end time.
      setEndTime(endTimeObj)

      updateForm([startTime, endTime])
    }
  }, [editTimesKey, selectedDate?.valueOf()])

  useEffect(() => {
    if (selectedDate) {
      const startTimeOptions = generateTimeOptions({
        type: 'start',
        shortestLeaveInterval,
        hourFormatString,
        minuteStep,
        workingHoursInDay: getWorkingHoursForEndCompute(),
      })
      setStartTimes(startTimeOptions)

      if (!editTimes) {
        const initialStartTimeObj = dayjs().hour(8).minute(0).second(0).millisecond(0)
        const initialStartTime = {
          value: safeToISOString(initialStartTimeObj),
          label: initialStartTimeObj.format(hourFormatString),
          key: safeToISOString(initialStartTimeObj),
        }
        setStartTime(initialStartTime)

        const startTimeValue = safeDayjs(initialStartTime?.value, initialStartTimeObj)

        // Generate end times immediately after setting start time
        const endTimeOptions = generateTimeOptions({
          type: 'end',
          shortestLeaveInterval,
          startTimeValue,
          hourFormatString,
          minuteStep,
          workingHoursInDay: getWorkingHoursForEndCompute(),
          isHalfDayHoliday: Boolean(isHalfDayHoliday()),
        })
        setEndTimes(endTimeOptions)
        handleTimeSelect([safeDayjs(initialStartTime.value, initialStartTimeObj)], undefined, { range: 'start' })
      }
      if (shouldResetTime) {
        setShouldResetTime(false)
      }
    }
    //Selected date is date object so use effect will not be called(valueOf is returning timestamp)
  }, [selectedDate?.valueOf(), editTimesKey, shouldResetTime, minuteStep])

  useEffect(() => {
    if (startTime && selectedDate) {
      const existingEndTime = endTime
      let newEndTime
      const startTimeValue = startTime.value

      const endTimeOptions = generateTimeOptions({
        type: 'end',
        shortestLeaveInterval,
        startTimeValue: safeDayjs(startTimeValue),
        hourFormatString,
        minuteStep,
        workingHoursInDay: getWorkingHoursForEndCompute(),
        isHalfDayHoliday: Boolean(isHalfDayHoliday()),
      })
      setEndTimes(endTimeOptions)
      if (shouldUpdateEndTime(startTime, existingEndTime)) {
        newEndTime = endTimeOptions.filter((option) => !option.disabled)[0]
        setEndTime(newEndTime as ITimeOption)
      }
      const endTimeValue = newEndTime?.value || existingEndTime?.value

      updateParentValue(startTimeValue, endTimeValue as string)
      handleTimeSelect([safeDayjs(startTimeValue), safeDayjs(endTimeValue as string)], undefined, { range: 'end' })
    }
  }, [startTime, editTimesKey])

  const shouldUpdateEndTime = (startTime: ITimeOption, existingEndTime: ITimeOption | null) => {
    if (!existingEndTime) return true

    const startMoment = safeDayjs(startTime.value).second(0).millisecond(0)
    const endMoment = safeDayjs(existingEndTime.value).second(0).millisecond(0)

    // Calculate if current end time is within valid range (in minutes)
    const diffMinutes = endMoment.diff(startMoment, 'minute')
    const maxWorkingMinutes = getWorkingHoursForEndCompute() * 60

    const isWithinWorkingWindow = diffMinutes > 0 && diffMinutes <= maxWorkingMinutes

    return !isWithinWorkingWindow || startMoment.isAfter(endMoment) || startMoment.isSame(endMoment)
  }

  const handleTimeSelect = (dates: Dayjs[] | null, dateStrings?: string[], info?: BaseInfo) => {
    if (!dates || !selectedDate) {
      return
    }
    const start = dates[0]
    if (start && info?.range === 'start' && shortestLeaveInterval === ShortestLeaveIntervalEnum.halfDay) {
      // Falls back to DEFAULT_WORKING_HOURS_IN_DAY when the selected day has no schedule (non-working
      // day, or the workHours prop hasn't arrived yet). Without this, halfDayMinutes would be 0 and
      // endTime would collapse onto startTime (previously visible as a stuck value on Teams mobile).
      const halfDayMinutes = getHalfDayDurationInMinutes(getWorkingHoursForEndCompute())
      const endTime = start.add(halfDayMinutes, 'minute')
      // When the auto-computed end lands on the next calendar day (e.g. 20:00 start + 4h),
      // display it as "24:00" / "12:00 AM" to match the appended end-of-day option label
      // and avoid showing a bare "00:00" that reads as today's midnight.
      const endsAtMidnight = endTime.isAfter(start, 'day')
      const midnightLabel = hourFormat === HourFormatEnum.twelve ? '12:00 AM' : '24:00'
      updateForm([start, endTime])
      setEndTime({
        key: safeToISOString(endTime),
        label: endsAtMidnight ? midnightLabel : endTime.format(hourFormat === HourFormatEnum.twelve ? 'hh:mm A' : 'HH:mm'),
        value: safeToISOString(endTime),
      })
      return
    }
    updateForm(dates)
  }

  // Duration check, not option-grid membership: an end the user already picked stays
  // valid for a new start as long as the leave is at least the policy minimum and fits
  // the day window (e.g. 1h policy, end 17:30, start moved to 14:00 → keep 17:30).
  const isEndValidForStart = (startTimeOption: ITimeOption, endTimeOption: ITimeOption): boolean => {
    const minMinutes = getMinIntervalMinutes(shortestLeaveInterval, getWorkingHoursForEndCompute())
    if (minMinutes === null) {
      return true
    }
    const diffMinutes = safeDayjs(endTimeOption.value).second(0).millisecond(0).diff(safeDayjs(startTimeOption.value).second(0).millisecond(0), 'minute')
    return diffMinutes >= minMinutes && diffMinutes <= getWorkingHoursForEndCompute() * 60
  }

  const handleStartTimeChange = (startTimeOption: ITimeOption) => {
    setStartTime(startTimeOption)
    // Clearing the end when it no longer fits the new start (before it, or leaving less
    // than the policy minimum) lets the startTime effect above auto-pick the first valid
    // option, i.e. new start + the policy's minimum duration.
    if (endTime && !isEndValidForStart(startTimeOption, endTime)) {
      setEndTime(null)
    }
    updateParentValue(startTimeOption.value, endTime?.value as string)
    handleTimeSelect([safeDayjs(startTimeOption.value)], undefined, { range: 'start' })
  }

  const handleEndTimeChange = (endTimeOption: ITimeOption) => {
    setEndTime(endTimeOption)
    updateParentValue(startTime?.value as string, endTimeOption.value)
    handleTimeSelect([safeDayjs(startTime?.value), safeDayjs(endTimeOption.value)], undefined, { range: 'end' })
  }

  const updateParentValue = (start: string | null, end: string | null) => {
    const formatTime = (time: string | null) => (time ? safeDayjs(time).get('hours').toString() : null)

    const startTime = formatTime(start)
    const endTime = formatTime(end)

    onChange?.({ startTime, endTime })
  }

  const handleClear = () => {
    updateForm([null, null])
    setStartTime(null)
    setEndTime(null)
    onChange?.({ startTime: null, endTime: null })
    handleTimeSelect(null, undefined, { range: 'start' })
  }

  const updateForm = (dates: (Dayjs | null)[]) => {
    if (!dates) {
      return
    }
    form.setFieldsValue({
      range: dates,
    })
  }

  const handleOnDropdownVisibleChange = (open: boolean, timeLabel: string | undefined, dropdownClassName: string) => {
    if (!open) {
      return
    }
    // Scroll on the first frame the dropdown's options exist. Retrying per frame instead
    // of waiting a fixed delay avoids painting the list at 00:00 and visibly jumping later.
    let attempts = 0
    const tryScroll = () => {
      // Scoped by popupClassName: a document-wide dropdown query can grab another
      // select's still-visible portal and scroll the wrong (hidden) list.
      const container = document.querySelector(`.${dropdownClassName}:not(.ant-select-dropdown-hidden)`) as HTMLElement
      if (container && scrollToSelectedOption(container, timeLabel || '')) {
        return
      }
      attempts += 1
      if (attempts < 40) {
        requestAnimationFrame(tryScroll)
      }
    }
    tryScroll()
  }

  const scrollToSelectedOption = (container: HTMLElement, timeLabel: string): boolean => {
    const selectedOption = Array.from(container.querySelectorAll('.ant-select-item-option-content')).find((el) => el.textContent === timeLabel) as HTMLElement | undefined

    if (!selectedOption) {
      return false
    }
    selectedOption.scrollIntoView({
      block: 'center',
    })
    // scrollIntoView can do nothing, with no error, inside the dropdown's animated/positioned portal,
    // so also center the option by setting the scrollable holder's scrollTop directly.
    const holder = container.querySelector('.rc-virtual-list-holder') as HTMLElement | null
    const optionRow = selectedOption.closest('.ant-select-item-option') as HTMLElement | null
    if (holder && optionRow) {
      holder.scrollTop = Math.max(0, optionRow.offsetTop - (holder.clientHeight - optionRow.offsetHeight) / 2)
    }
    return true
  }

  const handleOnMouseEnter = () => {
    if (startTime && endTime) {
      setHovered(true)
    }
  }

  const handleOnMouseLeave = () => {
    if (hovered) {
      setHovered(false)
    }
  }

  // The stored time can be off the current interval's grid (e.g. a 14:45 end from a
  // 15-minute policy edited after the policy switched to 30 minutes). It then matches no
  // option, so anchor the scroll to the nearest option instead.
  const getScrollAnchorLabel = (options: ITimeOption[], selected: ITimeOption | null): string | undefined => {
    if (!selected) {
      return undefined
    }
    if (options.some((option) => option.label === selected.label)) {
      return selected.label
    }
    const nearest = options.find((option) => option.value >= selected.value) || options[options.length - 1]
    return nearest?.label
  }

  return (
    <div className="custom-time-range-picker" style={style} onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave}>
      <Space>
        <Select<string, ITimeOption>
          popupMatchSelectWidth={100}
          className="time-select"
          style={{ width: 130 }}
          placeholder={<IntlMessages id="components.leaveForm.startTime" />}
          value={startTime as unknown as string}
          labelInValue
          onChange={(v) => handleStartTimeChange(v as unknown as ITimeOption)}
          options={startTimes}
          disabled={!selectedDate || shouldResetTime}
          suffixIcon={null}
          virtual={false}
          classNames={{ popup: { root: 'start-time-dropdown' } }}
          onOpenChange={(open) => handleOnDropdownVisibleChange(open, getScrollAnchorLabel(startTimes, startTime), 'start-time-dropdown')}
        />
        <SwapRightOutlined />
        <Select<string, ITimeOption>
          popupMatchSelectWidth={100}
          style={{ width: 130 }}
          className="time-select"
          placeholder={<IntlMessages id="components.leaveForm.endTime" />}
          value={endTime as unknown as string}
          labelInValue
          onChange={(v) => handleEndTimeChange(v as unknown as ITimeOption)}
          options={endTimes}
          suffixIcon={null}
          virtual={false}
          disabled={!startTime || shouldResetTime || !selectedDate || shortestLeaveInterval === ShortestLeaveIntervalEnum.halfDay}
          classNames={{ popup: { root: 'end-time-dropdown' } }}
          onOpenChange={(open) => handleOnDropdownVisibleChange(open, getScrollAnchorLabel(endTimes, endTime), 'end-time-dropdown')}
        />
      </Space>
      {hovered ? <CloseCircleFilled className="clear-icon" onClick={handleClear} /> : <ClockCircleOutlined className="clock-icon" />}
    </div>
  )
}

export default CustomTimeRangePicker
